import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { IconAlertTriangle, IconCheck, IconLockOpen } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/stat-card";
import { ScaleInput } from "@/components/weighing-form-shared";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useAuth } from "@/providers/auth-provider";
import { WING_SCALES, computeWing, type WingReading } from "@/domain/weigh-in";
import type { components } from "@/api/generated/schema";
import type { BoatId, WingConfig } from "@/types/identifiers";

type WingWeighing = components["schemas"]["WingWeighingResponse"];
type WingWeighingCreate = components["schemas"]["WingWeighingCreate"];
type WingWeighingUpdate = components["schemas"]["WingWeighingUpdate"];
type MeasurementInput = components["schemas"]["WingMeasurementInput"];

interface Props {
  boatId: BoatId;
  initialData?: WingWeighing;
  /** Newest weighing id at page open — drafts saved against an older value are discarded. */
  latestWeighingId: string | null;
  readOnly?: boolean;
  isSubmitting: boolean;
  isSigningOff: boolean;
  /** Runs the mutations; rejects on save failure so the form stays for retry.
   * A null payload means nothing changed in edit mode. */
  onSave: (
    payload: WingWeighingCreate | WingWeighingUpdate | null,
    withSignOff: boolean,
  ) => Promise<void>;
  onDone: () => void;
  onUnlock?: () => void;
  isUnlocking?: boolean;
}

const DRAFT_TTL_MS = 8 * 60 * 60 * 1000;
const CONFIGS: WingConfig[] = ["HAW", "APW", "LAW"];

// Wing weighings are single-set — one head/clew/butt reading per weighing.
interface FormState {
  config: WingConfig;
  mhu: boolean;
  screen: boolean;
  dicks: boolean;
  wand: boolean;
  notes: string;
  reading: WingReading;
}

const EMPTY_READING: WingReading = { head_kg: 0, clew_kg: 0, butt_kg: 0 };

const EMPTY_STATE: FormState = {
  config: "APW",
  mhu: true,
  screen: false,
  dicks: true,
  wand: false,
  notes: "",
  reading: { ...EMPTY_READING },
};

interface WingDraft extends FormState {
  savedAt: number;
  latestWeighingId: string | null;
}

function draftKey(boatId: BoatId, userSub: string): string {
  return `wing-draft-${boatId}-${userSub}`;
}

function loadInitialState(
  boatId: BoatId,
  userSub: string,
  latestWeighingId: string | null,
  initialData?: WingWeighing,
): { state: FormState; draftRestored: boolean } {
  if (initialData) {
    // Take the first recorded set (legacy weighings may carry more than one).
    const m = [...initialData.measurements].sort((a, b) => a.set_index - b.set_index)[0];
    return {
      state: {
        config: initialData.config as WingConfig,
        mhu: initialData.mhu_enabled,
        screen: initialData.screen_enabled,
        dicks: initialData.dicks_enabled,
        wand: initialData.wand_enabled,
        notes: initialData.notes ?? "",
        reading: m
          ? { head_kg: m.head_kg, clew_kg: m.clew_kg, butt_kg: m.butt_kg }
          : { ...EMPTY_READING },
      },
      draftRestored: false,
    };
  }

  const stored = localStorage.getItem(draftKey(boatId, userSub));
  if (stored) {
    try {
      const draft = JSON.parse(stored) as Partial<WingDraft>;
      const tooOld = Date.now() - (draft.savedAt ?? 0) > DRAFT_TTL_MS;
      const supersededByNewerSave = (draft.latestWeighingId ?? null) !== latestWeighingId;
      if (tooOld || supersededByNewerSave) {
        localStorage.removeItem(draftKey(boatId, userSub));
      } else if (draft.reading) {
        return {
          state: {
            config: (draft.config ?? "APW") as WingConfig,
            mhu: draft.mhu ?? true,
            screen: draft.screen ?? false,
            dicks: draft.dicks ?? true,
            wand: draft.wand ?? false,
            notes: draft.notes ?? "",
            reading: draft.reading,
          },
          draftRestored: true,
        };
      }
    } catch {
      localStorage.removeItem(draftKey(boatId, userSub));
    }
  }

  return { state: EMPTY_STATE, draftRestored: false };
}

function statesEqual(a: FormState, b: FormState): boolean {
  return (
    a.config === b.config &&
    a.mhu === b.mhu &&
    a.screen === b.screen &&
    a.dicks === b.dicks &&
    a.wand === b.wand &&
    a.notes === b.notes &&
    a.reading.head_kg === b.reading.head_kg &&
    a.reading.clew_kg === b.reading.clew_kg &&
    a.reading.butt_kg === b.reading.butt_kg
  );
}

function ToggleChip({
  label,
  enabled,
  disabled,
  onClick,
}: {
  label: string;
  enabled: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:cursor-default ${
        enabled
          ? "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 border border-emerald-500"
          : "bg-muted text-muted-foreground border border-transparent"
      } ${!disabled && !enabled ? "hover:border-border" : ""}`}
    >
      {label} {enabled ? "✓" : "—"}
    </button>
  );
}

const SCALE_ROWS: { label: string; key: keyof WingReading; coords: string }[] = [
  { label: "Head", key: "head_kg", coords: "x=2.1, z=23.7" },
  { label: "Clew", key: "clew_kg", coords: "x=3.5, z=0.43" },
  { label: "Butt", key: "butt_kg", coords: "x=0, z=0" },
];

export function WingWeighingForm({
  boatId,
  initialData,
  latestWeighingId,
  readOnly = false,
  isSubmitting,
  isSigningOff,
  onSave,
  onDone,
  onUnlock,
  isUnlocking = false,
}: Props) {
  const isEditMode = !!initialData;
  const { user } = useAuth();
  const userSub = user.sub;
  const init = useMemo(
    () => loadInitialState(boatId, userSub, latestWeighingId, initialData),
    [boatId, userSub, latestWeighingId, initialData],
  );

  const [state, setState] = useState<FormState>(init.state);

  const didToastRef = useRef(false);
  useEffect(() => {
    if (init.draftRestored && !didToastRef.current) {
      didToastRef.current = true;
      toast.info("Draft restored");
    }
  }, [init.draftRestored]);

  const initialState = useMemo(
    () => (isEditMode ? init.state : EMPTY_STATE),
    [isEditMode, init.state],
  );
  const isDirty = useMemo(() => !statesEqual(state, initialState), [state, initialState]);

  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (isEditMode) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    if (!isDirty) {
      localStorage.removeItem(draftKey(boatId, userSub));
      return;
    }
    draftTimerRef.current = setTimeout(() => {
      const draft: WingDraft = { ...state, savedAt: Date.now(), latestWeighingId };
      localStorage.setItem(draftKey(boatId, userSub), JSON.stringify(draft));
    }, 500);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [isEditMode, isDirty, state, boatId, userSub, latestWeighingId]);

  const handleConfirmDiscard = useCallback(() => {
    if (!isEditMode) localStorage.removeItem(draftKey(boatId, userSub));
  }, [isEditMode, boatId, userSub]);
  const { markCompleted } = useUnsavedChangesGuard(isDirty, handleConfirmDiscard);

  const scales = WING_SCALES[state.config];
  const hasScales = !!scales;
  const toggles = useMemo(
    () => ({ mhu: state.mhu, screen: state.screen, dicks: state.dicks, wand: state.wand }),
    [state.mhu, state.screen, state.dicks, state.wand],
  );

  const wingResult = useMemo(
    () => (hasScales && scales ? computeWing([state.reading], scales, toggles) : null),
    [state.reading, scales, hasScales, toggles],
  );

  const allCellsFilled =
    state.reading.head_kg > 0 && state.reading.clew_kg > 0 && state.reading.butt_kg > 0;
  const grossTotal = state.reading.head_kg + state.reading.clew_kg + state.reading.butt_kg;
  const canSignOff = hasScales && allCellsFilled;

  const updateCell = (key: keyof WingReading, value: number) => {
    setState((prev) => ({ ...prev, reading: { ...prev.reading, [key]: value } }));
  };

  const buildPayload = useCallback((): WingWeighingCreate | WingWeighingUpdate | null => {
    const measurementsArray: MeasurementInput[] = [
      { set_index: 1, ...state.reading, excluded: false },
    ];

    if (isEditMode && initialData) {
      const update: WingWeighingUpdate = {};
      if (state.mhu !== initialData.mhu_enabled) update.mhu_enabled = state.mhu;
      if (state.screen !== initialData.screen_enabled) update.screen_enabled = state.screen;
      if (state.dicks !== initialData.dicks_enabled) update.dicks_enabled = state.dicks;
      if (state.wand !== initialData.wand_enabled) update.wand_enabled = state.wand;
      if (state.notes !== (initialData.notes ?? "")) update.notes = state.notes;

      const first = [...initialData.measurements].sort((a, b) => a.set_index - b.set_index)[0];
      const measurementsChanged =
        !first ||
        initialData.measurements.length !== 1 || // collapse a legacy multi-set wing to one
        first.head_kg !== state.reading.head_kg ||
        first.clew_kg !== state.reading.clew_kg ||
        first.butt_kg !== state.reading.butt_kg;
      if (measurementsChanged) update.measurements = measurementsArray;

      return Object.keys(update).length === 0 ? null : update;
    }

    return {
      config: state.config,
      mhu_enabled: state.mhu,
      screen_enabled: state.screen,
      dicks_enabled: state.dicks,
      wand_enabled: state.wand,
      notes: state.notes || null,
      measurements: measurementsArray,
    };
  }, [state, isEditMode, initialData]);

  const handleSubmit = useCallback(
    async (withSignOff: boolean) => {
      try {
        await onSave(buildPayload(), withSignOff);
        markCompleted();
        localStorage.removeItem(draftKey(boatId, userSub));
        onDone();
      } catch {
        // Stay on the page; the action hook toasted the error.
      }
    },
    [onSave, buildPayload, markCompleted, boatId, userSub, onDone],
  );

  const busy = isSubmitting || isSigningOff;

  return (
    <div>
      {/* Live derived output — stat card row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Weight"
          value={wingResult ? `${wingResult.weight.toFixed(1)} kg` : "—"}
          accent={wingResult ? "border-l-emerald-500" : "border-l-border"}
          secondary={wingResult ? "net of toggles" : undefined}
        />
        <StatCard
          label="LCG"
          value={wingResult ? `${wingResult.lcg.toFixed(3)} m` : "—"}
          accent={wingResult ? "border-l-emerald-500" : "border-l-border"}
        />
        <StatCard
          label="VCG"
          value={wingResult ? `${wingResult.vcg.toFixed(3)} m` : "—"}
          accent={wingResult ? "border-l-emerald-500" : "border-l-border"}
        />
        <StatCard
          label="Sign-off gate"
          value={canSignOff ? "READY" : "—"}
          accent={canSignOff ? "border-l-emerald-500" : "border-l-border"}
          secondary={
            canSignOff
              ? "reading complete"
              : !hasScales
                ? "scale positions not configured"
                : "fill all 3 cells"
          }
          secondaryClass={canSignOff ? "text-emerald-700 dark:text-emerald-400" : undefined}
        />
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Derived live — single {state.config} reading, net of enabled accessories.
      </p>

      {/* Config + accessories */}
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Wing config
          </div>
          <div className="flex gap-1 bg-muted rounded-md p-1">
            {CONFIGS.map((c) => {
              const active = state.config === c;
              const disabled = readOnly || (isEditMode && c !== initialData?.config);
              return (
                <button
                  key={c}
                  type="button"
                  disabled={disabled}
                  onClick={() => setState((p) => ({ ...p, config: c }))}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                    active
                      ? "bg-card shadow ring-1 ring-border"
                      : disabled
                        ? "text-muted-foreground/40 cursor-not-allowed"
                        : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                  {c === "APW" && (
                    <span className="ml-1 text-[9px] font-normal text-emerald-700 dark:text-emerald-400">
                      canonical
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {!hasScales && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5">
              {state.config} scale positions are TBD — capture is supported, but sign-off requires positions to be configured.
            </p>
          )}
        </div>

        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Accessories on wing
          </div>
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              label="MHU (6.6 kg)"
              enabled={state.mhu}
              disabled={readOnly}
              onClick={() => setState((p) => ({ ...p, mhu: !p.mhu }))}
            />
            <ToggleChip
              label="Dicks (1.76/1.85/1.43)"
              enabled={state.dicks}
              disabled={readOnly}
              onClick={() => setState((p) => ({ ...p, dicks: !p.dicks }))}
            />
            <ToggleChip
              label="Screen (18.2 kg)"
              enabled={state.screen}
              disabled={readOnly}
              onClick={() => setState((p) => ({ ...p, screen: !p.screen }))}
            />
            <ToggleChip
              label="Wand (0.8 kg)"
              enabled={state.wand}
              disabled={readOnly}
              onClick={() => setState((p) => ({ ...p, wand: !p.wand }))}
            />
          </div>
        </div>
      </div>

      {/* Scale readings + notes */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div
          className={`bg-card border-l-3 ${
            allCellsFilled ? "border-l-emerald-500" : "border-l-border"
          } rounded-r-lg pl-4 pr-4 py-3`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Scale readings (kg)
          </div>
          <div className="space-y-1.5">
            {SCALE_ROWS.map((row) => (
              <label key={row.key} className="flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="block text-[11px] font-semibold text-foreground">
                    {row.label}
                  </span>
                  <span className="block text-[10px] tabular-nums text-muted-foreground">
                    {row.coords}
                  </span>
                </span>
                <span className="w-24 shrink-0">
                  <ScaleInput
                    value={state.reading[row.key]}
                    onChange={(v) => updateCell(row.key, v)}
                    disabled={readOnly}
                  />
                </span>
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-baseline justify-between border-t border-border pt-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Gross total
            </span>
            <span className="text-sm font-bold tabular-nums">
              {grossTotal > 0 ? grossTotal.toFixed(1) : "—"}{" "}
              <span className="text-[11px] font-medium text-muted-foreground">kg</span>
            </span>
          </div>
        </div>

        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Notes{" "}
            {!readOnly && (
              <span className="font-medium normal-case tracking-normal text-muted-foreground/70">
                (optional)
              </span>
            )}
          </div>
          {readOnly ? (
            <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">
              {state.notes || <span className="text-muted-foreground">—</span>}
            </p>
          ) : (
            <textarea
              value={state.notes}
              onChange={(e) => setState((p) => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. After repair to head batten cluster"
              className="mt-2 w-full p-2 text-sm border border-input bg-card rounded-md min-h-[60px] focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          )}
        </div>
      </div>

      {/* Action bar */}
      {readOnly ? (
        onUnlock && (
          <div className="mt-6 flex items-center gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={onUnlock} disabled={isUnlocking}>
              <IconLockOpen className="w-4 h-4 mr-1.5" />
              {isUnlocking ? "Unlocking…" : "Unlock to edit"}
            </Button>
          </div>
        )
      ) : (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <div
            className={`mr-auto inline-flex items-center gap-1.5 text-xs font-medium ${
              canSignOff ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"
            }`}
          >
            {canSignOff ? (
              <IconCheck className="w-3.5 h-3.5 shrink-0" />
            ) : (
              !hasScales && <IconAlertTriangle className="w-3.5 h-3.5 shrink-0" />
            )}
            {canSignOff
              ? "Ready to sign off — reading complete"
              : !hasScales
                ? "Scale positions not configured for this config — sign-off blocked"
                : "Fill all 3 cells to evaluate sign-off"}
          </div>
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => void handleSubmit(false)} disabled={busy}>
            {isSubmitting ? "Saving…" : isEditMode ? "Update draft" : "Save draft"}
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => void handleSubmit(true)}
            disabled={busy || !canSignOff}
            title={
              canSignOff ? undefined : "Sign-off requires configured scales and all cells filled"
            }
          >
            {isSigningOff ? "Signing off…" : "Save & sign off"}
          </Button>
        </div>
      )}
    </div>
  );
}
